import { Download, Upload } from '@mui/icons-material'
import { Alert, AlertTitle, Button, CircularProgress } from '@mui/material'
import { DialogConfirm } from 'components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getDownloadConfigFile, getUploadFileLogs, postUploadConfigFile } from 'services/apiKYE'
import { UPLOAD_FILE_NAMES } from 'utils/constants/config'
import { formatDateTime } from 'utils/functions'
import { downloadXlsx } from 'utils/downloadFile'
import { reportError } from 'utils/errorReporter'
import styles, { textFontSize } from './styles'

const FileManagerConfig = ({ fileType, baseItemsCount, loadingBase }) => {
  const token = useSelector((state) => state.auth.access_token)
  const [isLoadingLogs, setIsLoadingLogs] = useState(true)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [latestUploadLog, setLatestUploadLog] = useState({})
  const [successInfoOpen, setSuccessInfoOpen] = useState(true)
  const [failureInfoOpen, setFailureInfoOpen] = useState(true)
  const [fileIsBeingProcessed, setFileIsBeingProcessed] = useState(false)
  const [openConfirmUpload, setOpenConfirmUpload] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => {
    const fetchLatestLogs = () => {
      setIsLoadingLogs(true)
      getUploadFileLogs(token, fileType)
        .then((res) => {
          setIsLoadingLogs(false)
          setLatestUploadLog(res)
        })
        .catch((err) => {
          toast.error('Ocorreu um erro ao recuperar informações da base de dados.')
          reportError(err)
        })
    }
    fetchLatestLogs()
  }, [fileType])

  const onClickUploadFile = () => {
    setOpenConfirmUpload(true)
  }

  const onCloseConfirmUpload = () => {
    setSelectedFile(null)
    setOpenConfirmUpload(false)
  }

  const onSelectFile = (file) => {
    setSelectedFile(file)
  }

  const onSendFile = () => {
    postUploadConfigFile(token, selectedFile, fileType)
      .then(() => {
        toast.success('Estamos processando seu arquivo')
        setOpenConfirmUpload(false)
        setFileIsBeingProcessed(true)
      })
      .catch((err) => {
        toast.error('Houve um erro ao processar o arquivo. Verifique se está no formato correto e tente novamente.')
        reportError(err)
      })
  }

  const onDownloadFile = () => {
    setIsDownloadingFile(true)
    getDownloadConfigFile(token, fileType)
      .then((res) => {
        downloadXlsx(res, UPLOAD_FILE_NAMES[fileType])
      })
      .catch((err) => {
        toast.error('Houve um erro ao baixar o arquivo. Tente novamente mais tarde.')
        reportError(err)
      })
      .finally(() => setIsDownloadingFile(false))
  }

  const failureIsMoreRecentThanSuccess = () => {
    if (latestUploadLog?.success == null) {
      return true
    }
    return latestUploadLog?.failure?.created > latestUploadLog.success.created
  }

  const classes = styles()

  return (
    <div style={{ fontSize: textFontSize }}>
      <div>
        <div>
          <h3>Atualizar base via upload de arquivo</h3>

          <p>
            Atualize a base de {UPLOAD_FILE_NAMES[fileType]} enviando um arquivo XLSX. O formato do arquivo pode ser
            conferido ao baixar a base atual em XLSX.
          </p>
        </div>

        {!fileIsBeingProcessed && failureInfoOpen && latestUploadLog.failure && failureIsMoreRecentThanSuccess() && (
          <div className={classes.alertContainer}>
            <Alert
              sx={{ fontSize: textFontSize }}
              severity="error"
              variant="outlined"
              onClose={() => setFailureInfoOpen(false)}
            >
              <AlertTitle>Erro ao processar o arquivo de {UPLOAD_FILE_NAMES[fileType]}</AlertTitle>
              <p>
                A última tentativa de upload não foi bem sucedida. Verifique o conteúdo do arquivo enviado e tente
                novamente.
              </p>
              <br></br>
              <p>
                <b className={classes.bold}>Tentativa feita por:</b> {latestUploadLog.failure.user.name} (
                {latestUploadLog.failure.user.email})
              </p>
              <p>
                <b className={classes.bold}>Data:</b> {formatDateTime(latestUploadLog.failure.created)}
              </p>
              {latestUploadLog.failure.humanized_message && <p>
                <b className={classes.bold}>Detalhes do erro:</b> {latestUploadLog.failure.humanized_message}
              </p>}
            </Alert>
          </div>
        )}
      </div>

      {!fileIsBeingProcessed && successInfoOpen && latestUploadLog.success && (
        <div className={classes.alertContainer}>
          <Alert severity="info" color="primary" onClose={() => setSuccessInfoOpen(false)}>
            Última atualização da base via upload feita por{' '}
            <b className={classes.bold}>
              {latestUploadLog.success.user.name} ({latestUploadLog.success.user.email})
            </b>{' '}
            em <b className={classes.bold}>{formatDateTime(latestUploadLog.success.created)}</b>
          </Alert>
        </div>
      )}

      {fileIsBeingProcessed && (
        <div className={classes.alertContainer}>
          <Alert severity="success">
            Recebemos seu arquivo! Em instantes você receberá um email com o resultado da operação.
          </Alert>
        </div>
      )}

      <div className={classes.buttonsContainer}>
        <div className={classes.textWithbuttonDiv}>
          <span style={{ width: 200 }}>Base atual:</span>
          <span style={{ width: 300 }}>
            <Button
              className={classes.buttonStyle}
              disabled={isLoadingLogs || loadingBase}
              size="small"
              variant="contained"
              onClick={onDownloadFile}
              startIcon={isDownloadingFile ? <CircularProgress sx={{ color: 'white' }} size='1.7rem' /> : <Download />}
            >
              Baixar em XLSX
            </Button>
          </span>
        </div>

        <div className={classes.textWithbuttonDiv}>
          <span style={{ width: 200 }}>Atualizar a base:</span>
          <span style={{ width: 300 }}>
            <Button
              className={classes.buttonStyle}
              size="small"
              disabled={isLoadingLogs || loadingBase}
              variant="contained"
              onClick={onClickUploadFile}
              startIcon={<Upload></Upload>}
            >
              Fazer upload em XLSX
            </Button>
          </span>
        </div>
      </div>

      <div className={classes.dbInfoDiv}>
        {loadingBase ? (
          'Carregando informações da base atual...'
        ) : (
          <p>
            <b className={classes.bold}>{baseItemsCount}</b> registros encontrados na base atual.
          </p>
        )}
      </div>

      <DialogConfirm
        open={openConfirmUpload}
        btnConfirmText="Enviar"
        btnCancelText="Cancelar"
        onCancel={onCloseConfirmUpload}
        onConfirm={() => onSendFile()}
        btnConfirmDisabled={selectedFile == null}
        title="Atualizar base por arquivo"
      >
        <p>
          <b className={classes.bold}>Atenção:</b> ao atualizar a base de dados via upload de arquivo XLSX, você
          substituirá a base atual com os registros do novo arquivo.
        </p>

        <br></br>

        <input
          accept=".xls,.xlsx"
          style={{ fontSize: textFontSize }}
          type="file"
          onChange={(event) => onSelectFile(event.target.files[0])}
        />
      </DialogConfirm>
    </div>
  )
}

export default FileManagerConfig
